/* eslint-disable @typescript-eslint/no-explicit-any */
import { ShoriKb, HasuKb } from '@/constants/constants';
import moment from 'moment';
import BigNumber from 'bignumber.js';
import Encoding from 'encoding-japanese';

export const isShorikbADD = (shoriKb: number): boolean =>
  shoriKb === ShoriKb.ADD;

export const isShorikbREFER = (shoriKb: number): boolean =>
  shoriKb === ShoriKb.REFER;

export const isShorikbUPD = (shoriKb: number): boolean =>
  shoriKb === ShoriKb.UPD;

export const isShorikbCANCEL = (shoriKb: number): boolean =>
  shoriKb === ShoriKb.CANCEL;

export const isShorikbCOPY = (shoriKb: number): boolean =>
  shoriKb === ShoriKb.COPY;

export const isShorikbTEISEI = (shoriKb: number): boolean =>
  shoriKb === ShoriKb.TEISEI;

export const isShorikbDELCANCEL = (shoriKb: number): boolean =>
  shoriKb === ShoriKb.DEL_CANCEL;

export const isShorikbTEISEICANCEL = (shoriKb: number): boolean =>
  shoriKb === ShoriKb.TEISEI_CANCEL;

export const isShorikbIKKATSU = (shoriKb: number): boolean =>
  shoriKb === ShoriKb.IKKATSU;
// 加算(+)
export const add = (first: number, second: number): number => {
  const n1 = new BigNumber(first);
  const n2 = new BigNumber(second);
  return n1.plus(n2).toNumber();
};
// 減算(-)
export const subtract = (first: number, second: number): number => {
  const n1 = new BigNumber(first);
  const n2 = new BigNumber(second);
  return n1.minus(n2).toNumber();
};
// 乗算(*)
export const multiply = (first: number, second: number): number => {
  // 計算対象が０の場合、処理しない
  if (first === 0 || second === 0) {
    return 0;
  }
  const n1 = new BigNumber(first);
  const n2 = new BigNumber(second);
  return n1.times(n2).toNumber();
};
// 除算(/)
export const divide = (
  first: number,
  second: number,
  precision: number, // 残す桁数（小数3桁目を切り捨てして2桁まで残す場合は、2を設定する）
  hasuKb?: number // 端数区分を省略した場合、切り捨て
): number => {
  // 計算対象が０の場合、処理しない
  if (first === 0 || second === 0) {
    return 0;
  }
  // 端数区分を省略した場合、切り捨て
  const roundingMode = hasuKb ? hasuKb : HasuKb.KIRISUTE;
  const n1 = new BigNumber(first);
  const n2 = new BigNumber(second);
  switch (roundingMode) {
    case HasuKb.SHISHAGONYU:
      BigNumber.config({
        DECIMAL_PLACES: precision,
      });
      break;
    case HasuKb.KIRIAGE:
      BigNumber.config({
        DECIMAL_PLACES: precision,
        ROUNDING_MODE: BigNumber.ROUND_UP,
      });
      break;
    case HasuKb.KIRISUTE:
      BigNumber.config({
        DECIMAL_PLACES: precision,
        ROUNDING_MODE: BigNumber.ROUND_DOWN,
      });
      break;
    default:
      BigNumber.config({
        ROUNDING_MODE: BigNumber.ROUND_DOWN,
        DECIMAL_PLACES: precision,
      });
      break;
  }
  return n1.div(n2).toNumber();
};
// 剰余(%)
export const mod = (first: number, second: number): number => {
  // 計算対象が０の場合、処理しない
  if (first === 0 || second === 0) {
    return 0;
  }
  const n1 = new BigNumber(first);
  const n2 = new BigNumber(second);
  return n1.mod(n2).toNumber();
};

export const calcAmount = (
  tanka = 0,
  suryo = 0,
  hasu = 0,
  irisu = 0,
  bubiki = 0
): number => {
  const hasuTanka = hasu === 0 ? 0 : calcHasuTanka(tanka, irisu, bubiki);
  return Math.floor(
    add(multiply(subtract(tanka, bubiki), suryo), multiply(hasuTanka, hasu))
  );
};

export const calcHasuTanka = (
  tanka: number,
  irisu: number,
  bubiki = 0
): number => {
  const hasuTanka =
    irisu === 0
      ? 0
      : divide(subtract(tanka, bubiki), irisu, 2, HasuKb.KIRISUTE);
  return hasuTanka;
};

export const calcWeight = (
  caseWeight: number,
  suryo = 0,
  hasu = 0,
  irisu = 0
): number => {
  const hasuWeight =
    hasu !== 0 && irisu !== 0
      ? multiply(divide(caseWeight, irisu, 2, HasuKb.KIRISUTE), hasu)
      : 0;
  const weight = add(multiply(caseWeight, suryo), hasuWeight);
  return weight;
};

// 出来端数単価計算
export const calcDekiHasuTanka = (
  kingaku: number,
  irisu: number,
  suryo: number,
  hasuSuryo: number
): number => {
  let hasuTanka = 0;
  if (irisu === 0) {
    return 0;
  }
  const baraSuryo = add(multiply(suryo, irisu), hasuSuryo);
  hasuTanka = calcHasuTanka(kingaku, baraSuryo, 0);
  return hasuTanka;
};

// 出来単価計算
export const calcDekiTanka = (
  kingaku: number,
  suryo: number,
  hasuSuryo: number,
  hasuTanka: number
): number => {
  let tanka = 0;
  if (suryo === 0) {
    return 0;
  }
  const hasu = Math.floor(multiply(hasuSuryo, hasuTanka) * 100) / 100;
  const cs = Math.floor(subtract(kingaku, hasu) * 100) / 100;
  tanka = calcHasuTanka(cs, suryo, 0);
  return tanka;
};

export const dateStrToNum = (date: string): number => {
  if (!date) return 0;
  return +date.toString().replaceAll('-', '');
};

export const dateNumToStr = (date: number): string => {
  if (!date) return '';
  return moment(`${date}`).format().substring(0, 10);
};

export const dateFormat = (date: number, format: string): string => {
  return moment(date.toString()).format(format);
};

// Format number 000,000.00
export const formatNumber = ({
  value,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
}: {
  value: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}): string => {
  return value.toLocaleString('ja', {
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

// toFixedWithoutRounding(0.5839, 2)      => 0.58
export const toFixedWithoutRounding = (value: any, fixed: number) => {
  const v = (typeof value === 'string' ? value : value.toString()).split('.');
  if (fixed <= 0) return v[0];
  let f = v[1] || '';
  if (f.length > fixed) return `${v[0]}.${f.substr(0, fixed)}`;
  while (f.length < fixed) f += '0';
  return `${v[0]}.${f}`;
};

type pagerInfoType = {
  footerProps: {
    'items-per-page-text'?: string;
    'items-per-page-options'?: number[];
  };
  itemPerPage: number;
};
export function getDefaultPagerInfo(): pagerInfoType {
  const result = {
    footerProps: {},
    itemPerPage: 10,
  };
  const options = process.env.VUE_APP_TABLE_ITEMS_PER_PAGE_OPTIONS;
  if (!options) {
    console.log('環境変数が設定されていません');
  } else {
    result.footerProps = {
      'items-per-page-text': '表示件数：',
      'items-per-page-options': JSON.parse(options),
    };
  }
  const page = process.env.VUE_APP_TABLE_ITEMS_PER_PAGE;
  if (!page) {
    console.log('環境変数が設定されていません');
  } else {
    result.itemPerPage = +page;
  }
  return result;
}

export const removeBlankOrNullOrZero = (obj: any) => {
  Object.entries(obj).forEach(
    ([key, val]) =>
      (val && typeof val === 'object' && removeBlankOrNullOrZero(val)) ||
      ((val === null || val === '' || val === 0) && delete obj[key])
  );
  return obj;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const downloadCSV = (data: any, fileName: string) => {
  let csvContent = 'data:text/csv;charset=utf-8,';
  data.forEach(function (item: any) {
    // 半角カンマ→半角スペース、全角カンマ→全角スペースに置換
    // 改行削除
    const itemCopy = [...item];
    for( let i = 0; i < item.length; i++) {
      if (itemCopy[i] && (typeof itemCopy[i] === 'string')) {
        const val = itemCopy[i];
        itemCopy[i] = val.replace(/,/g, ' ').replace(/，/g, '　').replace('\r', '').replace('\n', '');
      }
    }
    const row = itemCopy.join(',');
    csvContent += row + '\r\n';
  });
  const encodedUri = encodeURI(csvContent);
  downloadFile(encodedUri, fileName);
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const downloadCSVToSjis = (data: any, fileName: string) => {
  // const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
  let csvContent = '';
  data.forEach(function (item: any) {
    // 半角カンマ→半角スペース、全角カンマ→全角スペースに置換
    // 改行削除
    const itemCopy = [...item];
    for( let i = 0; i < item.length; i++) {
      if (itemCopy[i] && (typeof itemCopy[i] === 'string')) {
        const val = itemCopy[i];
        itemCopy[i] = val.replace(/,/g, ' ').replace(/，/g, '　').replace('\r', '').replace('\n', '');
      }
    }
    const row = itemCopy.join(',');
    csvContent += row + '\r\n';
  });
  const uniArray = Encoding.stringToCode(csvContent);
  const sjisArray = Encoding.convert(uniArray, {
    to: 'SJIS',
    from: Encoding.detect(uniArray) as any,
  });
  const encodedUri = URL.createObjectURL(
    new Blob([new Uint8Array(sjisArray)], {
      type: 'text/csv;charset=Shift_JIS',
    })
  );
  downloadFile(encodedUri, fileName);
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const downloadFile = (url: string, fileName?: string) => {
  const defaultFileName = fileName
    ? fileName
    : (url.split('/').pop() as string);

  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('charset', 'Shift_JIS');
  link.setAttribute('download', defaultFileName);
  link.setAttribute('target', '_blank');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const isErrorDialogClosed = (errors: any) => {
  return errors.show == false && errors.messageID && errors.onConfirm == true;
};

export const convertStr2Num = (str: string) => {
  return str ? Number(str.toString().replace(/,/g, '')) : 0;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const findDuplicateByField = (itemList: any[], field: string) => {
  const findItemList: any[] = [];
  return itemList.reduce((dupList, item, index) => {
    const findIndex = findItemList.findIndex(
      (findItem) => findItem.value === item[field]
    );
    if (findIndex < 0) {
      findItemList.push({ value: item[field], index, isDup: false });
    } else {
      if (!findItemList[findIndex].isDup) {
        dupList.push(itemList[findItemList[findIndex].index]);
        findItemList[findIndex].isDup = true;
      }
      dupList.push(item);
    }
    return dupList;
  }, []);
};
export const convertNumToStr = (num: number) => {
  return num === null || num === 0 ? '' : num.toString();
};

export const getBase64 = (file: any) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export const checkDigitLength = (
  number: number,
  integer: number,
  decimal?: number
): boolean => {
  const numArr = `${number}`.replace('-', '').split('.');
  const intLength = numArr[0] ? numArr[0].length : 0;
  const decLength = numArr[1] ? numArr[1].length : 0;

  if (decimal === undefined) {
    return intLength <= integer;
  }

  const intCheckResult: boolean = intLength <= integer;
  const decCheckResult: boolean = decLength <= decimal;

  return intCheckResult && decCheckResult;
};

/**
 * 半角カナの入力値の検証
 * カナ
 * カナ＋濁点（ｶﾞｷﾞｸﾞｹﾞｺﾞ ｻﾞｼﾞｽﾞｾﾞｿﾞ ﾀﾞﾁﾞﾂﾞﾃﾞﾄﾞ ﾊﾞﾋﾞﾌﾞﾍﾞﾎﾞ ｳﾞ）
 * カナ＋半濁点（ﾊﾟﾋﾟﾌﾟﾍﾟﾎﾟ）
 * 数字 0～9
 * 英字 A～Z（小文字は不可）
 * 記号 ピリオド「.」、カッコ「()」、スペース「 」、音引「ｰ」、ハイフン「-」
 * @param {string} val 対象文字列
 * @returns {boolean} 検証結果
 */
export const chkHankakuKana = (val: string): boolean => {
  const regex =
    /^[.0-9A-Zｦ-ﾝｶﾞｷﾞｸﾞｹﾞｺﾞｻﾞｼﾞｽﾞｾﾞｿﾞﾀﾞﾁﾞﾂﾞﾃﾞﾄﾞﾊﾞﾋﾞﾌﾞﾍﾞﾎﾞｳﾞﾊﾟﾋﾟﾌﾟﾍﾟﾎﾟ .()-]+$/;
  return regex.test(val);
};

export const getExtensionFileFromBase64 = (string64: string) => {
  const extension = string64.substring(
    string64.indexOf('/') + 1,
    string64.indexOf(';base64')
  );

  return extension;
};

export const getImageFileFromBase64 = (string64: string, fileName: string) => {
  const extension = getExtensionFileFromBase64(string64);
  const trimmedString = string64.replace(
    /^data:image\/(png|jpg|jpeg|gif|tiff|webpp);base64,/,
    ''
  );
  const imageContent = atob(trimmedString);
  const buffer = new ArrayBuffer(imageContent.length);
  const view = new Uint8Array(buffer);

  for (let n = 0; n < imageContent.length; n++) {
    view[n] = imageContent.charCodeAt(n);
  }
  const type = `image/${extension}`;
  const blob = new Blob([buffer], { type });
  return new File([blob], fileName, {
    lastModified: new Date().getTime(),
    type,
  });
};

/**
 * 対象のコードを8桁に変換
 * <=5の場合は末尾に'000'を付け、8桁に満たない場合は先頭を0埋めする
 * >5の場合は先頭を0埋めする
 * @param code 
 */
export const padCodeToEightDigits = (code: string) => {
  if (code.length <= 5) {
    code = code + '000';
  }
  const paddedCode = code.padStart(8, '0');
  return paddedCode;
};